/* The structured block Brill emits when she drafts one block of a course module.
 *
 * The fourth instance of this pattern — offerBlock, sectionBlock, postBlock,
 * this. It is deliberately the same machinery, because the reason has not
 * changed: reading fields back out of Brill's paragraphs produced three separate
 * wrong-data bugs in `offerStore.js`, since the layout of a reply is a guideline
 * she follows loosely, not a contract. Nothing here is inferred. Brill states
 * each field on its own labelled line and we read exactly those; anything she
 * can't state she omits, and the field stays empty for the coach to fill.
 *
 * DRIVEN BY THE KIND SPEC. A module's blocks differ in shape, so this parser has
 * no field list of its own — it reads `courseBlockKinds.js`, which is also what
 * the editor renders and what gets sent to Brill in [MODULE_CONTEXT]. One
 * definition, three consumers.
 *
 *   <<<MODULE_DRAFTED>>>
 *   SEGMENT: Opening hook | 5 | Show of hands — who rewrote their about page this year?
 *   SEGMENT: Core teaching | 25 | The model on screen, one layer at a time.
 *   <<<MODULE_END>>>
 *
 * A single-value block is one line:
 *
 *   HOMEWORK: Write the first draft of your about page and bring it Tuesday.
 */

import {
  COURSE_BLOCK_KINDS, courseFieldLabel, courseFieldsOf, isCourseListField, courseRowsOf,
  blockKind, blockContent, withBlockContent, courseSpecLines,
} from "./courseBlockKinds";

const BLOCK = /<<<MODULE_DRAFTED>>>([\s\S]*?)<<<MODULE_END>>>/;

/* A label line: optional markdown decoration, the label, a colon, the value.
   Brill wraps labels in bold often enough (`**HOMEWORK:**`) that not allowing
   for it would drop whole fields. */
const LABEL_LINE = /^[ \t]*[*#>\-\s]*([A-Z_]{2,})[ \t]*\*{0,2}[ \t]*:[ \t]*(.*)$/;

/* A value that is only a bracketed placeholder is the template coming back —
   `HOMEWORK: [what she does next]`. That is not an answer, and storing it would
   put square brackets in her course. Treated as omitted. */
const PLACEHOLDER = /^\[[^\]]*\]$/;

/** Does this reply contain a drafted block at all? */
export function hasCourseBlock(text) {
  return BLOCK.test(text || "");
}

/**
 * The labelled entries inside a block, in the order Brill wrote them.
 *
 * Scanned line by line rather than matched per label, because list fields repeat
 * the SAME label and a per-label regex would find only the first SEGMENT. A
 * value runs until the next label, so a prose field keeps its line breaks.
 *
 * ANY label-shaped line ends the current value, including one this kind doesn't
 * have. Brill keeping a label from another block (`QUESTION:` while drafting
 * homework) is far likelier than a coach's sentence happening to start with a
 * word in capitals and a colon — and treating it as continuation would fold the
 * stray label and its value into the middle of the field above it, where she'd
 * find it in her course.
 */
function readEntries(body, labels) {
  const known = new Set(labels);
  const entries = [];
  let current = null;

  for (const line of String(body || "").split(/\r?\n/)) {
    const m = LABEL_LINE.exec(line);
    if (m) {
      const label = m[1].toUpperCase();
      current = known.has(label) ? { label, lines: [m[2]] } : null;
      if (current) entries.push(current);
    } else if (current) {
      current.lines.push(line);
    }
    // Lines before the first label are Brill's prose, not a field. Dropped.
  }

  return entries.map((e) => ({
    label: e.label,
    value: e.lines.join("\n").replace(/\*/g, "").trim(),
  }));
}

/** Split one list row on its separator, folding any extra segments back into the
 *  last part. Brill writing a teaching segment that itself contains a pipe must
 *  not silently lose the tail of it. */
function splitRow(value, parts) {
  const segments = value.split("|").map((s) => s.trim());
  const row = {};
  parts.forEach((p, i) => {
    const isLast = i === parts.length - 1;
    const raw = isLast ? segments.slice(i).join(" | ") : segments[i];
    const v = String(raw || "").trim();
    row[p.key] = PLACEHOLDER.test(v) ? "" : v;
  });
  return row;
}

/**
 * The fields Brill drafted, as a partial `content` object for this block kind,
 * or null when there's no block.
 *
 * Partial on purpose: a field Brill omitted is ABSENT from the returned object
 * rather than empty, which is what lets `applyCourseBlock` tell "she said
 * nothing" from "she said to clear it".
 */
export function parseCourseBlock(text, kindId) {
  const m = BLOCK.exec(text || "");
  if (!m) return null;

  const fields = courseFieldsOf(kindId);
  const byLabel = new Map(fields.map((f) => [courseFieldLabel(f), f]));
  const entries = readEntries(m[1], [...byLabel.keys()]);

  const content = {};
  for (const entry of entries) {
    const field = byLabel.get(entry.label);
    if (!field || !entry.value || PLACEHOLDER.test(entry.value)) continue;

    if (!isCourseListField(field)) {
      // Last one wins: if Brill restates a field inside one block, the
      // correction is the later line.
      content[field.key] = entry.value;
      continue;
    }
    const row = field.parts ? splitRow(entry.value, field.parts) : entry.value;
    const filled = field.parts ? Object.values(row).some(Boolean) : !!row;
    if (!filled) continue;
    content[field.key] = [...(content[field.key] || []), row];
  }
  return content;
}

/**
 * The reply with the block removed — what the coach reads in the chat.
 *
 * The fields are rendered as form inputs beside the conversation, so leaving the
 * block inline would show her the same content twice with delimiters around it.
 */
export function stripCourseBlock(text) {
  return (text || "")
    .replace(new RegExp(BLOCK.source, "g"), "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Fold a drafted block onto the block being edited.
 *
 * Her own words win over an omission — Brill leaving out HOMEWORK must never
 * blank homework she already wrote. A list Brill DID state replaces the stored
 * one rather than appending to it: she asked for four segments and got four, so
 * adding them to the four already there would double the outline every time she
 * asked for a rewrite.
 */
export function applyCourseBlock(content, block, kindId) {
  if (!block) return content || {};
  const next = { ...(content || {}) };
  for (const f of courseFieldsOf(kindId)) {
    const drafted = block[f.key];
    if (drafted === undefined) continue;
    if (isCourseListField(f)) {
      if (drafted.length) next[f.key] = drafted;
      continue;
    }
    if (String(drafted).trim()) next[f.key] = drafted;
  }
  return next;
}

/**
 * What Brill is told before she drafts: which module, which block, the exact
 * labels to answer under, what the module is FOR, and what the coach already has
 * in the block.
 *
 * Sent as its own leading turn rather than folded into the coach's message, the
 * same way the Offer Ladder sends [OFFER_CONTEXT] — the guide reads it as
 * instruction and she never sees it.
 *
 * THE OUTCOME IS THE BRIEF. A module is a session that produces one stated
 * result, so the outcome goes in whichever block is being written — the teaching
 * outline, the questions and the homework all exist to produce it. Drafting any
 * of them without it is guessing at what the session is for.
 */
export function buildCourseContext({ module, kindId, specLines = [] }) {
  const kind = blockKind(kindId);
  const lines = [
    "[MODULE_CONTEXT — read only, do not reference as a conversation]",
    `Module: ${module?.title || "Untitled module"}`,
    `Block: ${kind?.label || kindId} — ${kind?.hint || ""}`,
  ];

  /* The rest of the module, so a block is written into a session rather than on
     its own — homework that repeats the discussion questions is what happens
     when each block is drafted blind. The outcome leads because it is the brief;
     the others are context. */
  const outcome = String(module?.outcome || "").trim();
  lines.push(
    "",
    outcome
      ? `THE OUTCOME THIS MODULE PRODUCES — everything you write serves it:\n${outcome}`
      : "She has not written this module's outcome yet. Ask her what the module produces before drafting, or draft from the module's name and say plainly that you are guessing at the outcome.",
  );

  const around = surroundingLines(module, kindId);
  if (around.length) {
    lines.push("", "The rest of this module, so yours fits with it — do not rewrite these:", ...around);
  }

  lines.push(
    "",
    "Fill exactly these fields. Nothing else:",
    ...specLines,
  );

  const existing = existingLines(module, kindId);
  if (existing.length) {
    lines.push("", "What she already has in this block — edit it, don't start over:", ...existing);
  } else {
    lines.push("", "The block is empty. This is a first draft.");
  }
  return lines.join("\n");
}

/** The module's OTHER blocks, in one line each, so a draft sits inside a session
 *  rather than beside one. Trimmed short: this is orientation, not source. */
function surroundingLines(module, kindId) {
  const out = [];
  const order = Array.isArray(module?.blockOrder) ? module.blockOrder : [];
  for (const id of order) {
    if (id === kindId || id === "outcome") continue;   // the outcome has its own heading above
    const kind = blockKind(id);
    if (!kind) continue;
    const lines = existingLines(module, id);
    if (lines.length) out.push(`${kind.label}: ${lines.map(stripLabel).join(" / ")}`);
  }
  return out;
}

function stripLabel(line) {
  return line.replace(/^[A-Z_]{2,}:\s*/, "");
}

/** One block's current content, in the same labelled shape Brill writes back,
 *  so a rewrite is an edit of something rather than a guess at what's there. */
function existingLines(module, kindId) {
  const out = [];
  for (const f of courseFieldsOf(kindId)) {
    const content = blockContent(module, kindId);
    if (!isCourseListField(f)) {
      const v = String(content[f.key] ?? "").trim();
      if (v) out.push(`${courseFieldLabel(f)}: ${v}`);
      continue;
    }
    for (const row of courseRowsOf(content, f)) {
      const v = f.parts
        ? f.parts.map((p) => String(row?.[p.key] ?? "").trim()).join(" | ")
        : String(row ?? "").trim();
      if (v.replace(/\|/g, "").trim()) out.push(`${courseFieldLabel(f)}: ${v}`);
    }
  }
  return out;
}

/* ── THE WHOLE MODULE AT ONCE ─────────────────────────────────────────────────
   The block above is scoped to ONE kind, because the split screen writes one
   block beside its fields. The full-width module chat is the same conversation
   with more room: she talks about the session, and one reply can fill the
   outline AND the questions AND the homework.

   That works without a second format because THE FIVE KINDS' LABELS ARE UNIQUE
   ACROSS THE WHOLE MODULE — OUTCOME, SEGMENT, QUESTION, HOMEWORK, PREWORK. So a
   label names its block on its own, and one <<<MODULE_DRAFTED>>> block can carry
   several. If a sixth kind is ever added with a label that collides with one of
   these, this is what breaks, and the test below is what says so.             */

/** Every label Brill may use, mapped to the block and field it belongs to. */
function labelMap() {
  const out = new Map();
  for (const kind of COURSE_BLOCK_KINDS) {
    for (const field of kind.fields) out.set(courseFieldLabel(field), { kindId: kind.id, field });
  }
  return out;
}

/** The labels two different blocks share. Empty is the invariant; anything else
 *  means a reply can no longer be routed by label alone. */
export function collidingLabels() {
  const seen = new Map();
  const clashes = [];
  for (const kind of COURSE_BLOCK_KINDS) {
    for (const field of kind.fields) {
      const label = courseFieldLabel(field);
      if (seen.has(label) && seen.get(label) !== kind.id) clashes.push(label);
      else seen.set(label, kind.id);
    }
  }
  return clashes;
}

/**
 * A drafted block read as `{ [kindId]: contentPartial }` — every block Brill
 * filled in one reply, or null when there's no block at all.
 *
 * Same reading rules as `parseCourseBlock`; the only difference is that the
 * labels are the union of all five kinds rather than one kind's.
 */
export function parseModuleBlock(text) {
  const m = BLOCK.exec(text || "");
  if (!m) return null;

  const byLabel = labelMap();
  const entries = readEntries(m[1], [...byLabel.keys()]);

  const out = {};
  for (const entry of entries) {
    const target = byLabel.get(entry.label);
    if (!target || !entry.value || PLACEHOLDER.test(entry.value)) continue;
    const { kindId, field } = target;
    const content = out[kindId] || (out[kindId] = {});

    if (!isCourseListField(field)) {
      content[field.key] = entry.value;
      continue;
    }
    const row = field.parts ? splitRow(entry.value, field.parts) : entry.value;
    const filled = field.parts ? Object.values(row).some(Boolean) : !!row;
    if (!filled) continue;
    content[field.key] = [...(content[field.key] || []), row];
  }
  return out;
}

/**
 * The module with every drafted block folded in.
 *
 * A block Brill wrote that the module doesn't have yet is ADDED to it — drafting
 * pre-work in conversation should give her a pre-work block, not silently drop
 * the words. New blocks arrive in the kinds' own order; blocks she already has
 * keep the order she has them in, because that order is hers.
 */
export function applyModuleBlock(module, parsed) {
  if (!parsed || !Object.keys(parsed).length) return module;
  let next = { ...module };
  const order = Array.isArray(module?.blockOrder) ? [...module.blockOrder] : [];

  for (const kind of COURSE_BLOCK_KINDS) {
    const block = parsed[kind.id];
    if (!block) continue;
    const merged = applyCourseBlock(blockContent(next, kind.id), block, kind.id);
    next = withBlockContent(next, kind.id, merged);
    if (!order.includes(kind.id)) order.push(kind.id);
  }
  return { ...next, blockOrder: order };
}

/**
 * What Brill is told when the conversation is about the WHOLE module rather than
 * one block of it: the same brief, every block's labels, and which of them the
 * module already has.
 *
 * She is told she may fill several at once, and that a block she doesn't write
 * simply isn't mentioned — the alternative is a reply that emits all five labels
 * with three of them empty, which reads to the parser as nothing written.
 */
export function buildModuleContext({ module }) {
  const has = new Set(Array.isArray(module?.blockOrder) ? module.blockOrder : []);
  const lines = [
    "[MODULE_CONTEXT — read only, do not reference as a conversation]",
    `Module: ${module?.title || "Untitled module"}`,
    "Scope: the WHOLE module. You may write one block, several, or all of them in a single reply.",
  ];

  const outcome = String(module?.outcome || "").trim();
  lines.push(
    "",
    outcome
      ? `THE OUTCOME THIS MODULE PRODUCES — everything you write serves it:\n${outcome}`
      : "She has not written this module's outcome yet. That is the first thing to settle — ask her what the module produces, or propose one and say plainly that you are proposing it.",
  );

  lines.push("", "The blocks a module can have, and the labels each one is written under:");
  for (const kind of COURSE_BLOCK_KINDS) {
    lines.push(`— ${kind.label}${has.has(kind.id) ? " (she has this one)" : " (not in this module yet)"}`);
    for (const line of courseSpecLines(kind.id)) lines.push(`  ${line}`);
  }

  const existing = [];
  for (const kind of COURSE_BLOCK_KINDS) {
    if (!has.has(kind.id)) continue;
    const own = existingLines(module, kind.id);
    if (own.length) existing.push(`${kind.label}:`, ...own.map((l) => `  ${l}`));
  }
  if (existing.length) {
    lines.push("", "What she already has — edit it, don't start over, and leave alone whatever she didn't ask about:", ...existing);
  } else {
    lines.push("", "The module is empty. This is a first draft.");
  }

  lines.push(
    "",
    "Write only the blocks you actually have words for. Omit a label entirely rather than emitting it empty.",
  );
  return lines.join("\n");
}
